/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef, type ReactNode } from "react";
import * as Sentry from "@sentry/react";
import { supabase } from "@/integrations/supabase/client";
import type { User as SupabaseUser, Session } from "@supabase/supabase-js";
import { setUserId, trackEvent, trackPlaylist } from "@/lib/analytics";
import { useToast } from "@/hooks/use-toast";
import { safeSetItem, safeGetItem, safeRemoveItem } from "@/lib/utils";
import { readPlaybackPreference, writePlaybackPreference } from "@/lib/playbackPreference";
// playlistSaver + generationTracking are dynamic-imported below — see
// savePendingPlaylist. Static imports forced both libs (~30KB combined,
// plus their indirect deps) into the main bundle of every visitor, even
// though savePendingPlaylist only fires when a guest who generated a
// playlist signs in *and* still has a pendingPlaylist in localStorage —
// a small fraction of total sessions. Lazy-loading them shifts the cost
// to the post-auth recovery path. (Andy 2026-05-08 perf pass.)

interface Profile {
  id: string;
  display_name: string | null;
  avatar_url: string | null;
  /** Primary playback surface, synced across devices. null = unset (device
   *  localStorage default applies). Hydrated into localStorage on load. */
  playback_preference?: "spotify" | "youtube" | "apple_music" | null;
  /** Weekly-mix email opt-in. null = tier default (Pro on, Free off);
   *  true = on; false = off/unsubscribed. Read by useEmailPreferences. */
  weekly_mix_email?: boolean | null;
}

// 'limited_admin' added 2026-05-08 (migration 20260508210000). Phase 1 only
// expands the type — no permission checks consume it yet. Phase 2 (UI) and
// Phase 3 (page guards / RPC denylists) will use it to scope access.
export type UserRole = 'free' | 'pro' | 'admin' | 'limited_admin' | null;

/**
 * Whether a role carries Pro entitlements.
 *
 * Exported and pure so it can actually be tested. Until 2026-08-14 this rule
 * lived inline in the provider and had no coverage at all: the five tests that
 * looked like role coverage mock useAuth wholesale and then assert back the
 * `isPro` they supplied, so they pass whatever this function does.
 *
 * The boundary worth pinning is `limited_admin`, which is NOT Pro. It reads
 * like an admin variant, so the natural mistake is to fold it in with 'admin'
 * and hand out paid features.
 */
export function deriveIsPro(role: UserRole): boolean {
  return role === 'pro' || role === 'admin';
}

interface AuthContextType {
  user: SupabaseUser | null;
  profile: Profile | null;
  session: Session | null;
  loading: boolean;
  role: UserRole;
  roleLoading: boolean;
  isPro: boolean;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
  refreshRole: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// 2026-05-15 PM live-test root cause (test 5: reload after sign-in
// bounces to /auth): supabase-js's `getSession()` can take 5+ seconds
// to resolve even with a valid session in localStorage, because the
// internal auth lock is held by the synchronous onAuthStateChange
// callback running its own async work. The 5s boot watchdog then
// kicks in, sets user=null, and Playlists.tsx redirects to /auth.
//
// Fix: read the session DIRECTLY from localStorage at mount and
// optimistically populate user + session state synchronously. Route
// guards see a non-null user from the first render. Supabase-js's
// async validation later either confirms (no-op) or clears (real
// signOut). If localStorage is empty / corrupt, we fall back to null
// and the watchdog still backstops a true hang.
function readPersistedSession(): { session: Session | null; user: SupabaseUser | null } {
  try {
    if (typeof window === 'undefined' || !window.localStorage) {
      return { session: null, user: null };
    }
    // Find the sb-<ref>-auth-token key
    let raw: string | null = null;
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith('sb-') && k.endsWith('-auth-token')) {
        raw = localStorage.getItem(k);
        break;
      }
    }
    if (!raw) return { session: null, user: null };
    const parsed = JSON.parse(raw);
    const now = Math.floor(Date.now() / 1000);
    // Skip if access_token has expired more than 5 minutes ago. Within
    // 5 min of expiry we still trust it — supabase-js will refresh.
    if (typeof parsed?.expires_at === 'number' && parsed.expires_at < now - 300) {
      return { session: null, user: null };
    }
    if (parsed?.user?.id && parsed?.access_token) {
      return { session: parsed as Session, user: parsed.user as SupabaseUser };
    }
  } catch {
    // Corrupt JSON or storage unavailable — fall back to anon.
  }
  return { session: null, user: null };
}

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const persisted = readPersistedSession();
  const [user, setUser] = useState<SupabaseUser | null>(persisted.user);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [session, setSession] = useState<Session | null>(persisted.session);
  // Loading still starts true because role/profile haven't been fetched
  // yet — but user/session are pre-populated so route guards work.
  const [loading, setLoading] = useState(true);
  const [role, setRole] = useState<UserRole>(null);
  const [roleLoading, setRoleLoading] = useState(true);
  const roleRef = useRef<UserRole>(null);
  const lastUserIdRef = useRef<string | null>(null);
  const updateRole = (r: UserRole) => { setRole(r); roleRef.current = r; };
  const autoSaveAttemptedRef = useRef(false);
  const { toast } = useToast();

  const fetchRole = async (userId: string) => {
    try {
      setRoleLoading(true);
      const { data, error } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', userId);

      if (error) {
        updateRole('free');
      } else if (data && data.length > 0) {
        const roles = data.map(r => r.role);
        if (roles.includes('admin')) {
          updateRole('admin');
        } else if (roles.includes('pro')) {
          updateRole('pro');
        } else {
          updateRole('free');
        }
      } else {
        updateRole('free');
      }
    } catch {
      updateRole('free');
    } finally {
      setRoleLoading(false);
    }
  };

  const fetchProfile = async (userId: string) => {
    try {
      const { data } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .maybeSingle();

      if (data) {
        // The DB column is generic text (`string | null`); the domain type
        // narrows it to the three valid surfaces. Anything else (legacy/garbage)
        // collapses to null = unset.
        const accountPref: Profile["playback_preference"] =
          data.playback_preference === "spotify" ||
          data.playback_preference === "youtube" ||
          data.playback_preference === "apple_music"
            ? data.playback_preference
            : null;
        setProfile({
          id: data.id,
          display_name: data.display_name,
          avatar_url: data.avatar_url,
          playback_preference: accountPref,
        });
        // Hydrate the device playback preference from the account (the
        // cross-device source of truth). Only when the account holds an
        // explicit value that DIFFERS from the device; NULL = unset, keep the
        // device default. writePlaybackPreference emits the change event so
        // the header toggle + account tiles update live. This does NOT write
        // back to the DB (that path is the hook's setPreference), so there's
        // no echo loop.
        if (accountPref && readPlaybackPreference() !== accountPref) {
          writePlaybackPreference(accountPref);
        }
      }
    } catch {
      // Profile fetch is non-critical — app works without it
    }
  };

  const refreshProfile = useCallback(async () => {
    if (user) {
      await fetchProfile(user.id);
    }
  }, [user]);

  const savePendingPlaylist = async (userId: string) => {
    // ATOMIC LOCK: Prevent concurrent save operations
    const saveInProgress = safeGetItem('playlist_save_in_progress');
    if (saveInProgress) {
      return false;
    }

    const pendingPlaylistStr = safeGetItem('pendingPlaylist');
    if (!pendingPlaylistStr) return false;
    
    // Set lock before starting save
    safeSetItem('playlist_save_in_progress', Date.now().toString());
    
    try {
      const pendingPlaylist = JSON.parse(pendingPlaylistStr);
      
      // CRITICAL: Check if pending playlist belongs to current user
      // If userId is present and doesn't match, this is from a different session - skip it
      if (pendingPlaylist.userId && pendingPlaylist.userId !== userId) {
        safeRemoveItem('pendingPlaylist');
        safeRemoveItem('playlist_save_in_progress');
        return false;
      }
      
      // CRITICAL: Check if pending playlist is stale (> 30 minutes old)
      // This prevents cross-user contamination in testing/multi-user environments
      if (pendingPlaylist.timestamp) {
        const ageMinutes = (Date.now() - pendingPlaylist.timestamp) / 1000 / 60;
        if (ageMinutes > 30) {
          safeRemoveItem('pendingPlaylist');
          safeRemoveItem('playlist_save_in_progress');
          return false;
        }
      }
      
      // SANITIZE: Replace invalid characters from old pending playlists
      // Edge function regex: /^[\w\s\-'.,!&()]+$/ (allows single quotes, not double quotes)
      let playlistName = pendingPlaylist.playlistName || 'My Playlist';
      playlistName = playlistName.replace(/"/g, "'"); // Replace double quotes with single quotes
      playlistName = playlistName.replace(/[^\w\s\-'.,!&()]/g, ''); // Remove any other invalid chars
      playlistName = playlistName.trim().substring(0, 100); // Ensure max length

      // Lazy-import the save + tracking libs at the recovery path. See header
      // comment for the rationale — this keeps them off the main bundle.
      const [{ savePlaylist }, { markGenerationSuccess }] = await Promise.all([
        import("@/lib/playlistSaver"),
        import("@/lib/generationTracking"),
      ]);

      const result = await savePlaylist({
        userId,
        playlistName,
        tracks: pendingPlaylist.tracks,
        prompt: pendingPlaylist.prompt,
        parameters: pendingPlaylist.parameters,
        // `parameters.model` is the tier seed the request ASKED for, not what
        // ran. Same cached object as usePlaylistRecovery, same fix.
        model: pendingPlaylist.actualModelUsed,
        spotifyPlaylistId: pendingPlaylist.spotifyPlaylistId,
        spotifyUri: pendingPlaylist.spotifyUri,
        // PRIVACY (2026-08-22). Omitting this used to publish the playlist:
        // `is_public: !(isPro || usedProModel)` with both undefined is `true`,
        // so every Pro user who generated signed out and then signed in to
        // save got a public playlist tagged free.
        //
        // `roleRef.current` is null only before fetchRole resolves — every
        // outcome of that fetch, error included, writes a non-null role. So
        // null is a true "not answered yet", and passing `undefined` for it
        // routes to derivePlaylistVisibility's unresolved-means-private
        // branch rather than being mistaken for a free user.
        //
        // fetchRole is kicked off ~140 lines above without an await, so it has
        // usually landed by the time this runs; when it has not, private is
        // the recoverable direction.
        isPro: roleRef.current === null ? undefined : deriveIsPro(roleRef.current),
        // The server's visibility union counts a coin BALANCE, and this is
        // the one save path with no hook to read it from: useCredits calls
        // useAuth, so calling it here would be circular. One query, on a
        // path that already makes several.
        //
        // Fails CLOSED on purpose: an error or a missing row leaves this
        // undefined, which derivePlaylistVisibility treats as unresolved and
        // therefore private. Coalescing to 0 here would republish a coin
        // holder's playlist on a transient failure.
        coinBalance: await (async () => {
          try {
            const { data, error } = await supabase
              .from('user_credits')
              .select('balance')
              .eq('user_id', userId)
              .maybeSingle();
            if (error) return undefined;
            return data?.balance ?? 0;
          } catch {
            return undefined;
          }
        })(),
      });

      if (!result.success) {
        throw new Error(result.error || 'Failed to save playlist');
      }

      const playlistId = result.playlistId ?? "";

      // Only clear localStorage after successful save
      safeRemoveItem('pendingPlaylist');
      safeRemoveItem('auth_redirect');
      
      // Update generation tracking status if attemptId was provided
      if (pendingPlaylist.attemptId) {
        const duration = pendingPlaylist.timestamp ? Date.now() - pendingPlaylist.timestamp : 0;

        const spotifyPlaylistId =
          typeof result.spotifyPlaylistId === "string"
            ? result.spotifyPlaylistId
            : typeof pendingPlaylist.spotifyPlaylistId === "string"
              ? pendingPlaylist.spotifyPlaylistId
              : "";

        await markGenerationSuccess(pendingPlaylist.attemptId, {
          playlist_id: playlistId,
          spotify_playlist_id: spotifyPlaylistId,
          final_tracks_count: pendingPlaylist.tracks?.length || 0,
          final_tracks: Array.isArray(pendingPlaylist.tracks)
            ? pendingPlaylist.tracks.map((t: { name?: string; artist?: string }) => ({
                name: t?.name ?? "",
                artist: t?.artist ?? "",
              }))
            : undefined,
          duration_ms: duration,
        });
      }
      
      // Track the save that happened via the post-login pending playlist flow
      trackPlaylist("save_playlist_completed", playlistId, "post_login_auto_save");

      // Navigate to playlists with openPlaylist param to auto-open modal
      const playlistUrl = `/playlists?openPlaylist=${playlistId}`;
      window.history.pushState({}, '', playlistUrl);
      window.dispatchEvent(new PopStateEvent('popstate'));
      
      toast({ 
        title: 'Welcome!', 
        description: 'Your playlist has been saved and is now open.',
        duration: 3500,
      });
      
      return true;
    } catch {
      autoSaveAttemptedRef.current = false; // Allow retry on failure
      toast({ 
        title: 'Playlist saved locally', 
        description: 'Visit the Generate page to complete saving',
        duration: 3500
      });
      return false;
    } finally {
      // Always remove lock, even on error
      safeRemoveItem('playlist_save_in_progress');
    }
  };

  // This listener intentionally initializes once and internally handles auth state changes.
  useEffect(() => {
    let cancelled = false;

    // Set up auth state listener FIRST.
    //
    // CANONICAL PATTERN per Supabase docs (validated via supabase-kb
    // 2026-05-15 PM, similarity 0.82+):
    //   "Avoid using async functions as callbacks. Limit the number of
    //   await calls in async callbacks. Do not use other Supabase
    //   functions in the callback function. If you must, dispatch the
    //   functions once the callback has finished executing."
    //
    // The reason this matters: supabase-js holds an internal auth
    // Navigator-Lock while the onAuthStateChange callback is running.
    // Any `await supabase.X()` inside the callback re-enters the same
    // lock and serializes; concurrent `getSession()` / RPC calls then
    // queue behind it. Live-test on 2026-05-15 PM (test 5: reload
    // after sign-in) showed getSession() taking 5+ seconds because the
    // old async callback was awaiting setUserId → fetchRole → fetchProfile
    // → claim_anon_playlists → savePendingPlaylist → process-referral
    // all inline. The 5s boot watchdog then fired and the route guard
    // on Playlists.tsx bounced the user to /auth.
    //
    // Fix: the callback is SYNCHRONOUS. Only state setters, refs, and
    // localStorage writes run inline. All await-able work — every
    // supabase.* call, every dynamic import that touches supabase,
    // analytics, and so on — moves into setTimeout(..., 0), which
    // schedules a fresh macrotask. By the time that macrotask runs,
    // the callback has returned and the auth lock is released.
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (cancelled) return;

        // ---- SYNC SECTION ----
        // 2026-05-15 PM live-test finding (test 5: reload after login):
        // supabase-js fires INITIAL_SESSION with session=null BEFORE
        // finishing the storage read, then fires it AGAIN (or fires
        // SIGNED_IN/TOKEN_REFRESHED) with the real session. If we set
        // loading=false on the first null event, route guards like
        // Playlists.tsx see `!user && !authLoading` and bounce the
        // authenticated user to /auth before the real session lands.
        // Only mark loading=false when the event is definitive: a
        // truthy session means "we know who you are", and any
        // non-INITIAL_SESSION event means supabase-js has finished
        // its storage read. The getSession().then handler below is
        // the other path that resolves loading.
        if (session || event !== 'INITIAL_SESSION') {
          setLoading(false);
        }
        setSession(session);
        setUser(session?.user ?? null);

        if (!session?.user) {
          // Signed-out path — everything here is sync.
          setProfile(null);
          updateRole(null);
          lastUserIdRef.current = null;
          setRoleLoading(false);
          // Clear Sentry user on sign-out so subsequent errors aren't
          // mis-attributed to the previous user. Audit 2026-05-10.
          Sentry.setUser(null);
          return;
        }

        // Capture for the deferred closure — the `session` reference is
        // bound to this callback invocation, not later state.
        const sessionUser = session.user;

        // Attach the user to Sentry so future errors carry the user_id for
        // dashboard filtering. Email is included because instrument.ts has
        // sendDefaultPii: true. Calling setUser before Sentry.init() is safe
        // — the global Hub buffers the user and applies it once init wires
        // the transport (instrument.ts defers init to requestIdleCallback).
        // Audit 2026-05-10.
        Sentry.setUser({ id: sessionUser.id, email: sessionUser.email });

        // Skip role/profile re-fetch when role is already known for the
        // same user. onAuthStateChange fires multiple events on page load
        // (INITIAL_SESSION, SIGNED_IN, TOKEN_REFRESHED) — refetching each
        // time causes roleLoading flashes that unmount AdminRoute children.
        const userChanged = sessionUser.id !== lastUserIdRef.current;
        const shouldFetchRole = userChanged || !roleRef.current;
        if (shouldFetchRole) {
          lastUserIdRef.current = sessionUser.id;
        }

        // Handle password recovery flow — redirect to Auth page to show
        // "set new password" form. Pure sync work, do it inline + return.
        if (event === 'PASSWORD_RECOVERY') {
          safeSetItem('password_recovery_pending', 'true');
          window.location.href = '/auth';
          return;
        }

        // First-time-user detection — set the welcome flag so the
        // <WelcomeMounter /> in App.tsx renders DashboardWelcome on this
        // session. Heuristic: user.created_at and user.last_sign_in_at
        // within 60s of each other = first session (signup just happened,
        // either email-verification redirect or OAuth first sign-in).
        // Returning users have lastSignIn >> createdAt.
        //
        // Idempotent: WelcomeMounter clears the flag after the welcome
        // completes, so subsequent SIGNED_IN events for the same user
        // don't re-set it. Even if heuristic mis-fires (very rare clock
        // skew), localStorage is per-user so the impact is one extra
        // welcome play that the user can skip.
        if (event === 'SIGNED_IN' && sessionUser.created_at && sessionUser.last_sign_in_at) {
          const createdAtMs = new Date(sessionUser.created_at).getTime();
          const lastSignInMs = new Date(sessionUser.last_sign_in_at).getTime();
          const isFirstSession = Math.abs(lastSignInMs - createdAtMs) < 60_000;
          if (isFirstSession) {
            safeSetItem(`spotivibly:pending-welcome:${sessionUser.id}`, 'true');
          }
        }

        // Guard the SIGNED_IN-driven async branch (anon-inbox claim,
        // pendingPlaylist save, referral) sync so a re-entry during the
        // deferred await chain is impossible. autoSaveAttemptedRef is a
        // session-scoped one-shot.
        const isFirstSignedInEvent = event === 'SIGNED_IN' && !autoSaveAttemptedRef.current;
        if (isFirstSignedInEvent) {
          autoSaveAttemptedRef.current = true;
        }

        // ---- DEFERRED ASYNC SECTION ----
        // setTimeout(..., 0) schedules a fresh macrotask so the callback
        // returns before any await runs. This is the canonical pattern.
        setTimeout(async () => {
          if (cancelled) return;

          // Set user ID for analytics tracking
          await setUserId(sessionUser.id, sessionUser.email || undefined);
          if (cancelled) return;

          // Role + profile fetch (only if user changed since last event)
          if (shouldFetchRole) {
            fetchProfile(sessionUser.id);
            fetchRole(sessionUser.id);
          }

          // SIGNED_IN-only block — anon-inbox claim, pendingPlaylist save,
          // referral, redirects. Gated by the sync guard above.
          if (!isFirstSignedInEvent) return;

          // Anon-inbox claim — re-attribute any pre-signup anon playlists
          // (created via create_anon_playlist) to this user. Runs once
          // per sign-in event. Best-effort: errors don't block the rest
          // of the sign-in flow. See `src/lib/anonFingerprint.ts` +
          // migration 20260514100000.
          //
          // S16-AUD-031 / audit-2 C3 (2026-05-14): this MUST run + settle
          // BEFORE savePendingPlaylist. Pre-fix it ran as a `void` IIFE
          // in parallel; the localStorage pendingPlaylist (set by Save
          // click while anon) and the anon-row claim are usually the
          // SAME generation, so racing them produced two `playlists`
          // rows for one gen. By awaiting the claim and then dedup'ing
          // pendingPlaylist against claimed rows by prompt, we keep
          // exactly one row.
          interface ClaimedRow {
            id: string;
            prompt: string | null;
            name: string | null;
            spotify_playlist_id: string | null;
            created_at: string;
          }
          let claimedRows: ClaimedRow[] = [];
          try {
            const { getAnonFingerprint, clearAnonFingerprint } = await import("@/lib/anonFingerprint");
            const fingerprint = getAnonFingerprint();
            const { data: claimed, error: claimErr } = await supabase.rpc(
              "claim_anon_playlists",
              { p_anon_fingerprint: fingerprint },
            );
            if (claimErr) {
              console.warn("[anon-inbox] claim failed:", claimErr.message);
            } else if (Array.isArray(claimed) && claimed.length > 0) {
              claimedRows = claimed as ClaimedRow[];
              // Anon-claim funnel step 3 of 4 (shown → clicked → CLAIMED →
              // delivered). The label carries "<claimed>/<still pending
              // Spotify>" so the funnel can split clean claims from ones
              // the backfill retry still has to finish. "Delivered" is
              // measured in SQL (playlists.spotify_playlist_id landing on
              // claimed rows), not as a client event.
              trackEvent(
                "anon_claim_completed",
                "anon_claim",
                `${claimedRows.length}/${claimedRows.filter((r) => !r.spotify_playlist_id).length}`,
                {
                  claimed_count: claimedRows.length,
                  pending_spotify_count: claimedRows.filter((r) => !r.spotify_playlist_id).length,
                },
              );
              // Clear the fingerprint so a later sign-out + new-anon
              // session doesn't re-attempt claiming the same rows.
              clearAnonFingerprint();
              toast({
                title: claimedRows.length === 1 ? "Welcome, your playlist is saved" : `Welcome, ${claimedRows.length} playlists added to your library`,
                description: "We carried over what you made before signing up.",
                duration: 5000,
              });
            }
          } catch (err) {
            console.warn("[anon-inbox] claim threw:", err instanceof Error ? err.message : err);
          }

          const hasPendingPlaylist = safeGetItem('pendingPlaylist');
          const hasPendingReferral = safeGetItem('pending_referral');

          // Dedup: if claim already absorbed this exact generation
          // (same prompt + name in the just-claimed set), the pending
          // localStorage save is redundant — both refer to the same
          // anon gen. Drop pendingPlaylist instead of inserting a
          // duplicate `playlists` row.
          let pendingPlaylistAlreadyClaimed = false;
          if (hasPendingPlaylist && claimedRows.length > 0) {
            try {
              const pending = JSON.parse(hasPendingPlaylist) as {
                playlistName?: string;
                prompt?: string;
              };
              const normalize = (s: string | null | undefined) =>
                (s ?? "").trim().toLowerCase().slice(0, 200);
              const wantPrompt = normalize(pending.prompt);
              const wantName = normalize(pending.playlistName);
              if (wantPrompt || wantName) {
                pendingPlaylistAlreadyClaimed = claimedRows.some(
                  (r) =>
                    normalize(r.prompt) === wantPrompt ||
                    normalize(r.name) === wantName,
                );
              }
            } catch {
              // pendingPlaylist JSON malformed — fall through to normal save
            }
          }

          if (pendingPlaylistAlreadyClaimed) {
            safeRemoveItem('pendingPlaylist');
            safeRemoveItem('auth_redirect');
            // Open the claimed playlist so the user lands on their gen
            // immediately, matching the savePendingPlaylist UX. Pick the
            // most recently created match — claimed_rows are returned
            // in DESC order from claim_anon_playlists already.
            try {
              const pending = JSON.parse(hasPendingPlaylist ?? "{}") as {
                playlistName?: string;
                prompt?: string;
              };
              const normalize = (s: string | null | undefined) =>
                (s ?? "").trim().toLowerCase().slice(0, 200);
              const target =
                claimedRows.find(
                  (r) =>
                    normalize(r.prompt) === normalize(pending.prompt) ||
                    normalize(r.name) === normalize(pending.playlistName),
                ) ?? claimedRows[0];
              if (target) {
                const playlistUrl = `/playlists?openPlaylist=${target.id}`;
                window.history.pushState({}, '', playlistUrl);
                window.dispatchEvent(new PopStateEvent('popstate'));
                safeSetItem('restoreGeneratedPlaylist', 'true');
              }
            } catch {
              // Navigation is best-effort; user can still find the
              // claimed playlist in /playlists.
            }
          }

          // Process playlist save first (if exists AND not already
          // covered by an anon-inbox claim).
          if (hasPendingPlaylist && !pendingPlaylistAlreadyClaimed && session.access_token) {
            // Save the pending playlist (no redirect needed - stays on current page)
            const saved = await savePendingPlaylist(sessionUser.id);
            if (saved) {
              // Set flag for Generate page to show success toast
              safeSetItem('restoreGeneratedPlaylist', 'true');
            }
            // Don't return early - continue to process referral if present
          }

          // CRITICAL: Process pending referral code after signup/signin
          // This runs INDEPENDENTLY of playlist save (both can happen in same session)
          if (hasPendingReferral && session.access_token) {
            try {
              const { data, error } = await supabase.functions.invoke('process-referral', {
                headers: { Authorization: `Bearer ${session.access_token}` },
                body: { referralCode: hasPendingReferral }
              });

              if (error) {
                // silent — referral retry on next sign-in
              } else if (data?.success) {
                safeRemoveItem('pending_referral');
                toast({
                  title: "Referral applied!",
                  description: data.message || "Generate your first playlist to earn rewards for your friend.",
                  duration: 3500,
                });
              } else {
                // Don't remove from localStorage - allow retry on next sign-in
              }
            } catch (_error) {
              // silent — allow retry on next sign-in
            }
          }

          // Handle any redirects if no playlist was pending
          if (!hasPendingPlaylist) {
            const authRedirect = safeGetItem('auth_redirect');
            const authPrompt = safeGetItem('auth_prompt');

            // Validate redirect is a relative path to prevent open redirect attacks
            const isRelativePath = (path: string) => path.startsWith('/') && !path.startsWith('//');

            if (authRedirect && isRelativePath(authRedirect)) {
              safeRemoveItem('auth_redirect');
              const redirectPath = authRedirect;

              if (authPrompt) {
                safeRemoveItem('auth_prompt');
                const url = `${redirectPath}?prompt=${encodeURIComponent(authPrompt)}`;
                window.history.pushState({}, '', url);
                window.dispatchEvent(new PopStateEvent('popstate'));
              } else {
                window.history.pushState({}, '', redirectPath);
                window.dispatchEvent(new PopStateEvent('popstate'));
              }
            } else if (authRedirect) {
              // Invalid redirect path, just clean up
              safeRemoveItem('auth_redirect');
              safeRemoveItem('auth_prompt');
            }
          }
        }, 0);
      }
    );

    // THEN check for existing session (just for initial loading state).
    // Role + profile fetching is handled by onAuthStateChange above —
    // calling fetchRole here too causes a double roleLoading flash that
    // unmounts AdminRoute children.
    // .catch() required: Supabase fetch (Auth API uses fetch under the hood)
    // rejects with `TypeError: Load failed` on iPhone Safari when the network
    // blips OR when the user backgrounds the tab during initial page load.
    // Without catch the rejection bubbles to global unhandledrejection and
    // pollutes client_errors. AuthContext is mounted on EVERY route — this
    // was the highest-blast-radius leak. Treat as "no session" on failure;
    // the onAuthStateChange listener will pick up the session when network
    // returns.
    //
    // 2026-05-15 PM: Andy "reloading breaks the login state and nothing
    // loads. only force delete cache and 3p cookies resolves this."
    // Root cause: getSession() can HANG indefinitely when supabase-js
    // tries to auto-refresh a stale/expired token (autoRefreshToken=true).
    // The .then/.catch chain only fires if the promise SETTLES — if it
    // hangs, setLoading(false) is never called, so every route that
    // gates content on `loading === false` (which is all of them via
    // AuthRoute / useEffect-on-user-id) sits on the skeleton forever.
    // 5s watchdog: if getSession hasn't settled, force into anonymous
    // state. onAuthStateChange will still pick up the real session if
    // and when it eventually resolves (Supabase fires SIGNED_IN or
    // TOKEN_REFRESHED events independently of getSession's promise).
    let bootSettled = false;
    // 2026-05-15 PM: the watchdog now ONLY resolves loading; it does NOT
    // clobber user/session. Pre-fix, this watchdog called a
    // `finishBootAsAnon` helper which set user=null even when the optimistic
    // localStorage read had populated user with a valid session. That
    // bounced authenticated users to /auth whenever getSession() took
    // >5s to resolve (which it does when supabase-js's auth lock is
    // held by an in-flight onAuthStateChange callback). If user is
    // already populated from the persisted-session read, leave it
    // alone — onAuthStateChange will correct it later if invalid.
    const finishBootKeepingUser = (reason: string) => {
      if (cancelled || bootSettled) return;
      bootSettled = true;
      if (reason) console.warn(`[AuthContext] boot watchdog: ${reason}`);
      setLoading(false);
      // Don't touch session/user — they were optimistically populated
      // from localStorage and are the source of truth until an explicit
      // onAuthStateChange event (SIGNED_OUT / TOKEN_REFRESHED / etc.)
      // tells us otherwise. roleLoading also stays true so route guards
      // that depend on it wait for the real role fetch.
    };
    const bootWatchdog = setTimeout(() => {
      finishBootKeepingUser("getSession() did not settle within 5s; preserving persisted user");
    }, 5_000);

    supabase.auth.getSession().then(async ({ data: { session }, error }) => {
      if (cancelled || bootSettled) return;
      clearTimeout(bootWatchdog);
      bootSettled = true;
      // If there's an error or invalid session, clear everything
      if (error) {
        try {
          await supabase.auth.signOut({ scope: 'local' });
        } catch {
          // signOut can also reject on network failure — local cleanup below
          // is what matters.
        }
        if (cancelled) return;
        setSession(null);
        setUser(null);
        setProfile(null);
        setLoading(false);
        setRoleLoading(false);
        return;
      }

      setSession(session);
      setUser(session?.user ?? null);

      if (!session?.user) {
        updateRole(null);
        setRoleLoading(false);
      }

      setLoading(false);
    }).catch(() => {
      // Network/timeout failure on getSession() itself. Preserve the
      // optimistically-restored user/session from localStorage — they
      // were good enough to render the app. onAuthStateChange will
      // correct the state when connectivity returns. Pre-2026-05-15
      // this called finishBootAsAnon and clobbered the user, causing
      // network-blip page loads to bounce authenticated users to /auth.
      clearTimeout(bootWatchdog);
      finishBootKeepingUser("");
    });

    return () => {
      cancelled = true;
      clearTimeout(bootWatchdog);
      subscription.unsubscribe();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty deps - only run once on mount

  const signOut = useCallback(async () => {
    // Clear local state FIRST to ensure UI updates immediately
    setUser(null);
    setProfile(null);
    setSession(null);

    // CRITICAL: Clear user-specific data to prevent cross-user contamination
    safeRemoveItem('pendingPlaylist');
    safeRemoveItem('pendingGeneratedPlaylist');
    safeRemoveItem('playlist_save_in_progress');
    safeRemoveItem('pending_referral');
    safeRemoveItem('auth_redirect');
    safeRemoveItem('auth_prompt');
    safeRemoveItem('restoreGeneratedPlaylist');

    try {
      // 2026-05-15 PM: switched from bare `supabase.auth.signOut({ scope:
      // 'local' })` to the forceClearSupabaseAuth helper. The bare call
      // waits on the Navigator-locks auth lock and was leaving the
      // sb-*-auth-token in localStorage when the lock was held by an
      // in-flight onAuthStateChange callback (live-test: after clicking
      // Sign Out, URL changed but localStorage still had the session,
      // so the next reload re-optimistically-restored the "signed out"
      // user). forceClearSupabaseAuth purges localStorage SYNCHRONOUSLY
      // before racing signOut against a 1.5s timeout.
      const { forceClearSupabaseAuth } = await import('@/integrations/supabase/client');
      await forceClearSupabaseAuth();
    } catch (_error) {
      // silent — local state already cleared
    } finally {
      // Use SPA-friendly navigation instead of full page reload
      window.history.pushState({}, '', '/');
      window.dispatchEvent(new PopStateEvent('popstate'));
    }
  }, []);

  const refreshRole = useCallback(async () => {
    if (user?.id) await fetchRole(user.id);
  }, [user?.id]);

  const isPro = deriveIsPro(role);

  const value = useMemo(() => ({
    user, profile, session, loading, role, roleLoading, isPro, signOut, refreshProfile, refreshRole
  }), [user, profile, session, loading, role, roleLoading, isPro, signOut, refreshProfile, refreshRole]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    // Safe fallback to avoid crashes before provider mounts (e.g., on hot reloads or edge cases)
    return {
      user: null,
      profile: null,
      session: null,
      loading: true,
      role: null,
      roleLoading: true,
      isPro: false,
      signOut: async () => {},
      refreshProfile: async () => {},
      refreshRole: async () => {},
    } satisfies AuthContextType;
  }
  return context;
};
